import { z } from 'zod';
import { ReleaseEnvironmentSchema } from '../release-products/shapes';
import { UserRefSchema } from '../user-ref';

export const RELEASE_STATUSES = [
  'draft',
  'running',
  'succeeded',
  'failed',
  'publishing',
  'published',
] as const;
export const ReleaseStatusSchema = z.enum(RELEASE_STATUSES);
export type ReleaseStatus = z.infer<typeof ReleaseStatusSchema>;

export const WORKFLOW_FILE_PATTERN = /^[A-Za-z0-9._-]+\.(yml|yaml)$/;

/** Workflow files (from .github/workflows at the pinned SHA) the draft will dispatch. */
export const SelectedWorkflowsSchema = z
  .array(z.string().regex(WORKFLOW_FILE_PATTERN, 'must be a .yml/.yaml workflow filename'))
  .max(20)
  .refine((files) => new Set(files).size === files.length, {
    message: 'duplicate workflow file',
  });

/** Per-workflow free-form Draft inputs: { [workflowFile]: { inputName: value } }. */
export const WorkflowInputOverridesSchema = z.record(z.record(z.string()));
export type WorkflowInputOverrides = z.infer<typeof WorkflowInputOverridesSchema>;

export const ReleaseRowSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  productSlug: z.string(),
  productName: z.string(),
  environment: ReleaseEnvironmentSchema,
  version: z.string(),
  buildNumber: z.number().int(),
  status: ReleaseStatusSchema,
  ref: z.string(),
  commitSha: z.string(),
  /** Free-text annotation; editable in every status. */
  description: z.string(),
  failureReason: z.string(),
  /** Draft whose version ≤ the channel's published floor: viewable, delete-only. */
  isStale: z.boolean(),
  createdBy: UserRefSchema,
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
});
export type ReleaseRow = z.infer<typeof ReleaseRowSchema>;

export const WORKFLOW_RUN_STATUSES = [
  'pending',
  'dispatched',
  'running',
  'succeeded',
  'failed',
] as const;
export const WorkflowRunStatusSchema = z.enum(WORKFLOW_RUN_STATUSES);
export type WorkflowRunStatus = z.infer<typeof WorkflowRunStatusSchema>;

/** Why a run failed (or why reconcile couldn't resolve it) — captured best-effort from GitHub. */
export const WorkflowRunFailureDetailSchema = z.object({
  summary: z.string(),
  /** GitHub run conclusion (failure, cancelled, timed_out, …); null for non-run notes. */
  conclusion: z.string().nullable(),
  jobs: z.array(
    z.object({
      name: z.string(),
      htmlUrl: z.string(),
      failedSteps: z.array(z.string()),
      annotations: z.array(z.object({ level: z.string(), message: z.string() })),
      /** Size-capped tail of the job's log. */
      logTail: z.string(),
    }),
  ),
});
export type WorkflowRunFailureDetail = z.infer<typeof WorkflowRunFailureDetailSchema>;

/** One terminal attempt of a workflow run; history survives retries (UCH-453). */
export const WorkflowRunAttemptSchema = z.object({
  attemptNumber: z.number().int(),
  status: WorkflowRunStatusSchema,
  htmlUrl: z.string(),
  failureDetail: WorkflowRunFailureDetailSchema.nullable(),
  /** True once the full GitHub logs zip is archived; the S3 key stays server-side. */
  logArchiveAvailable: z.boolean(),
  completedAt: z.string(),
});
export type WorkflowRunAttempt = z.infer<typeof WorkflowRunAttemptSchema>;

export const WorkflowRunInfoSchema = z.object({
  id: z.string().uuid(),
  workflowFile: z.string(),
  status: WorkflowRunStatusSchema,
  htmlUrl: z.string(),
  githubRunId: z.number().nullable(),
  githubRunAttempt: z.number().int(),
  failureDetail: WorkflowRunFailureDetailSchema.nullable(),
  /** Terminal attempts, ascending attemptNumber. */
  attempts: z.array(WorkflowRunAttemptSchema),
  updatedAt: z.string(),
});
export type WorkflowRunInfo = z.infer<typeof WorkflowRunInfoSchema>;

export const DEPLOYMENT_ARGOCD_PHASES = ['pending', 'syncing', 'healthy', 'failed'] as const;

/** Latest Publish deployment (Publish = deploy, 0012); null when Publish never deployed. */
export const ReleaseDeploymentSchema = z.object({
  /** Deploy workflow run state. */
  status: WorkflowRunStatusSchema,
  /** Exact run URL once claimed, else the workflow's Actions page. */
  runUrl: z.string(),
  /** null when the channel deploys without an ArgoCD step. */
  argocdPhase: z.enum(DEPLOYMENT_ARGOCD_PHASES).nullable(),
  /** GitOps env-branch head ArgoCD must report Synced + Healthy at. */
  expectedRevision: z.string().nullable(),
  error: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type ReleaseDeployment = z.infer<typeof ReleaseDeploymentSchema>;

export const ReleaseArtifactInfoSchema = z.object({
  id: z.string().uuid(),
  platform: z.string(),
  arch: z.string(),
  filename: z.string(),
  sizeBytes: z.number().int(),
  packageId: z.string(),
  uploadedAt: z.string(),
  /**
   * Download URL: public CDN for published releases, short-TTL presigned S3
   * GET for unpublished builds; '' when release storage is unconfigured.
   */
  url: z.string(),
});
export type ReleaseArtifactInfo = z.infer<typeof ReleaseArtifactInfoSchema>;

/** Cross-product version requirement — informational, never a Confirm/Publish gate. */
export const ReleaseRequirementSchema = z.object({
  productId: z.string().uuid(),
  productName: z.string(),
  version: z.string(),
});
export type ReleaseRequirement = z.infer<typeof ReleaseRequirementSchema>;

/** One release in a promotion chain (UCH-436), ordered dev→sit→uat→prd. */
export const PromotionHopSchema = z.object({
  id: z.string().uuid(),
  environment: ReleaseEnvironmentSchema,
  version: z.string(),
  buildNumber: z.number().int(),
  status: ReleaseStatusSchema,
  commitSha: z.string(),
  /** Who pressed Promote to create this release; null for the chain's origin. */
  promotedBy: UserRefSchema.nullable(),
  createdAt: z.string(),
});
export type PromotionHop = z.infer<typeof PromotionHopSchema>;

export const ReleaseDetailSchema = ReleaseRowSchema.extend({
  selectedWorkflows: z.array(z.string()),
  workflowInputOverrides: WorkflowInputOverridesSchema,
  /** Non-empty = auto Publish to testers on success (UCH-513); draft-set. */
  autoDistributeGroups: z.array(z.string()),
  /** Persisted diff-baseline build; changelog `against=baseline` defaults to it. */
  baseReleaseId: z.string().uuid().nullable(),
  requirements: z.array(ReleaseRequirementSchema),
  versionFloor: z.string().nullable(),
  runs: z.array(WorkflowRunInfoSchema),
  artifacts: z.array(ReleaseArtifactInfoSchema),
  /** Succeeded + required-artifact matrix satisfied (empty matrix = no gate). */
  readyToPublish: z.boolean(),
  missingArtifacts: z.array(z.object({ platform: z.string(), arch: z.string() })),
  /** Head commit subject/url from the Confirm-time capture (github_commits); '' when absent. */
  commitSubject: z.string(),
  commitUrl: z.string(),
  /** Confirm stored injected apiUrl/uploadToken for the gated reveal (UCH-296). Values never travel here. */
  secretsAvailable: z.boolean(),
  /** Set when this release was created by Promote — its ref+commitSha are locked to the source. */
  promotedFromReleaseId: z.string().uuid().nullable(),
  /** Promoted draft whose source has a pinned ref+SHA — ref/commitSha edits are 409'd. */
  refLocked: z.boolean(),
  /**
   * Promotion chain this release belongs to (UCH-436), following
   * promoted_from links both directions, ordered dev→sit→uat→prd.
   * Empty when the release has no promote links.
   */
  promotionTrail: z.array(PromotionHopSchema),
  /** Description edit history, newest first; `by` null when the author was removed. */
  descriptionHistory: z.array(
    z.object({
      description: z.string(),
      by: UserRefSchema.nullable(),
      at: z.string(),
    }),
  ),
  /** Caller opted in to Slack DMs for this release (UCH-806). */
  watching: z.boolean(),
  deployment: ReleaseDeploymentSchema.nullable(),
  /** Publish-to-testers history, newest first (UCH-423). */
  distributions: z.array(
    z.object({
      platforms: z.array(z.string()),
      groupAliases: z.array(z.string()),
      by: UserRefSchema.nullable(),
      at: z.string(),
    }),
  ),
});
export type ReleaseDetail = z.infer<typeof ReleaseDetailSchema>;
